import { S3_SOURCE } from "./s3-list";
import type { Detection } from "./types";

export type FrpPoint = { t: number; mw: number };

/** Max FRP per overpass — the whole event, not a rolling day. */
export function frpEventSeries(detections: Detection[]): FrpPoint[] {
  return collapseByOverpass(detections);
}

/** @deprecated use frpEventSeries — kept so older tests still import a name. */
export function frpSeries24h(detections: Detection[], _nowMs?: number): FrpPoint[] {
  return frpEventSeries(detections);
}

/** Last overpass at or before the map playhead — panel FRP while scrubbing. */
export function frpAtOrBefore(detections: Detection[], playheadMs: number): number | null {
  return frpAtOrBeforeSeries(frpEventSeries(detections), playheadMs);
}

/** Same walk as frpAtOrBefore, on a series that is already collapsed. */
export function frpAtOrBeforeSeries(series: FrpPoint[], playheadMs: number): number | null {
  let best: FrpPoint | null = null;
  for (const p of series) {
    if (p.t <= playheadMs && (!best || p.t > best.t)) best = p;
  }
  return best?.mw ?? null;
}

function collapseByOverpass(detections: Detection[]): FrpPoint[] {
  const byTime = new Map<string, number>();
  for (const d of detections) {
    // Sentinel-3 has no MW — do not plot a fake 0 on the FRP chart.
    if (d.source === S3_SOURCE || d.frp_mw == null) continue;
    const mw = d.frp_mw;
    byTime.set(d.observed_at, Math.max(byTime.get(d.observed_at) ?? 0, mw));
  }
  return [...byTime.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([iso, mw]) => ({ t: Date.parse(iso), mw }))
    .filter((p) => Number.isFinite(p.t));
}

/** Only public http(s) webcam pages — drop OSM tags that are "yes" or javascript: URLs. */
export function publicHttpUrl(raw: string | undefined | null): string | null {
  const s = (raw ?? "").trim();
  if (!s || s === "yes" || s === "no") return null;
  try {
    const u = new URL(s);
    if (u.protocol !== "http:" && u.protocol !== "https:") return null;
    if (u.hostname === "localhost" || u.hostname.endsWith(".local")) return null;
    return u.toString();
  } catch {
    return null;
  }
}
